import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronRight } from 'lucide-react';
import { saveKoffieSession } from '../koffiereparatie-geleen/KoffieApp';
import { saveWhenconnectSession } from '../whenconnect/WhenconnectApp';
import { initKmfcClient } from '../lib/supabase';
import WebvonkLogoImage from '../assets/Webvonk.png';
import { wv } from '../login/LoginScreen';

const COMPANIES = [
  {
    slug: 'kmfc-shop',
    name: 'KMFC Shop',
    meta: 'Bestellingen · Menu · Statistieken',
    url: '/#/KMFC/orders',
    logo: 'https://kmfcshoponline.nl/assets/logo.png',
  },
  {
    slug: 'koffiereparatie-geleen',
    name: 'Koffiereparatie Geleen',
    meta: 'Machines · Reparaties · Contactinzendingen',
    url: '/#/Koffiereparatie/dashboard',
    logo: '/assets/Koffie-geleen_logo.png',
  },
  {
    slug: 'whenconnect',
    name: 'Whenconnect',
    meta: 'HR · Mediation · Coaching',
    url: '/#/Whenconnect/dashboard',
    logo: null,
  },
];

function getSuperAdmin() {
  try { return JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return null; }
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${wv.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px ${wv.px};
  -webkit-font-smoothing: antialiased;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 400px;
  animation: ${fadeUp} 0.35s ease both;
`;

const LogoWrap = styled.div`
  margin-bottom: 40px;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
  filter: brightness(0);
`;

const PageLabel = styled.span`
  display: block;
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
  margin-bottom: 10px;
`;

const PageTitle = styled.h1`
  font-family: ${wv.fontSerif};
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 900;
  font-style: italic;
  color: ${wv.text};
  letter-spacing: -0.02em;
  line-height: 0.95;
  margin: 0 0 28px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  background: transparent;
  border: 1.5px solid ${wv.lines};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-family: ${wv.fontSans};
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: ${wv.text}; background: rgba(12,12,11,0.02); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const CompanyLogoWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 7px;
  background: ${wv.bg};
  border: 1px solid ${wv.lines};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const CompanyLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const CompanyInitials = styled.div`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 700;
  color: ${wv.grey};
  letter-spacing: 0.04em;
`;

const ItemInfo = styled.div`flex: 1; text-align: left;`;

const ItemName = styled.div`
  font-family: ${wv.fontSans};
  font-size: 13px;
  font-weight: 600;
  color: ${wv.text};
`;

const ItemMeta = styled.div`
  font-family: ${wv.fontSans};
  font-size: 11px;
  color: ${wv.grey};
  margin-top: 2px;
`;

const ItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ActiveBadge = styled.div`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
  border: 1px solid ${wv.lines};
  border-radius: 4px;
  padding: 2px 7px;
`;

const Chevron = styled.div`
  color: ${wv.lines};
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${wv.lines};
`;

const UserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${wv.text};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  font-family: ${wv.fontSans};
  letter-spacing: 0.06em;
  color: ${wv.bg};
  text-transform: uppercase;
`;

const UserName = styled.div`
  font-family: ${wv.fontSans};
  font-size: 12px;
  font-weight: 500;
  color: ${wv.body};
`;

const LogoutBtn = styled.button`
  background: none;
  border: none;
  border-bottom: 1px solid ${wv.lines};
  padding: 0 0 2px;
  cursor: pointer;
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${wv.body};
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: ${wv.text}; border-color: ${wv.text}; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12,12,11,0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const Modal = styled.div`
  background: ${wv.bg};
  border: 1.5px solid ${wv.lines};
  border-radius: 8px;
  padding: 28px 24px 22px;
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ModalLabel = styled.span`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
`;

const ModalTitle = styled.h2`
  font-family: ${wv.fontSerif};
  font-size: 24px;
  font-weight: 900;
  font-style: italic;
  color: ${wv.text};
  margin: 0 0 8px;
  letter-spacing: -0.02em;
`;

const ModalText = styled.p`
  font-family: ${wv.fontSans};
  font-size: 13px;
  color: ${wv.body};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`display: flex; gap: 8px;`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 6px;
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  border: 1.5px solid ${p => p.$danger ? wv.text : wv.lines};
  background: ${p => p.$danger ? wv.text : 'transparent'};
  color: ${p => p.$danger ? wv.bg : wv.body};

  &:hover {
    background: ${p => p.$danger ? '#1a1a18' : 'rgba(12,12,11,0.04)'};
    color: ${p => p.$danger ? wv.bg : wv.text};
  }
`;

export default function CompanySelect() {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [currentSlug, setCurrentSlug] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    const sa = getSuperAdmin();
    if (!sa) { window.location.href = '/'; return; }
    setSuperAdmin(sa);

    try {
      const koffie = JSON.parse(localStorage.getItem('koffie_session') || 'null');
      if (koffie?.company_slug) { setCurrentSlug(koffie.company_slug); return; }
    } catch {}
    try {
      const kmfc = JSON.parse(localStorage.getItem('kmfc_user') || 'null');
      if (kmfc?.company_slug) { setCurrentSlug(kmfc.company_slug); return; }
    } catch {}
  }, []);

  const switchTo = async (company) => {
    if (switching) return;
    setSwitching(company.slug);
    const base = {
      id: superAdmin.id,
      username: superAdmin.username,
      company_slug: company.slug,
      company_name: company.name,
      role_id: 1,
      role: { slug: 'admin', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
      access_token: superAdmin.access_token,
      refresh_token: superAdmin.refresh_token,
      expires_at: superAdmin.expires_at,
      login_time: new Date().toISOString(),
    };
    if (company.slug === 'koffiereparatie-geleen') {
      saveKoffieSession(base);
      window.location.href = company.url;
    } else if (company.slug === 'whenconnect') {
      saveWhenconnectSession(base);
      window.location.hash = '/Whenconnect/dashboard';
      window.location.reload();
    } else {
      await initKmfcClient();
      localStorage.setItem('kmfc_user', JSON.stringify({ ...base, expires_at: superAdmin.expires_at }));
      window.location.hash = '/KMFC/orders';
      window.location.reload();
    }
  };

  const logout = () => {
    localStorage.removeItem('webvonk_superadmin');
    localStorage.removeItem('koffie_session');
    localStorage.removeItem('kmfc_user');
    window.location.href = '/';
  };

  if (!superAdmin) return null;

  const initials = (superAdmin.username || 'WV').slice(0, 2).toUpperCase();

  return (
    <Page>
      <Inner>
        <LogoWrap>
          <Logo src={WebvonkLogoImage} alt="Webvonk" />
        </LogoWrap>

        <PageLabel>Klantportaal</PageLabel>
        <PageTitle>Kies een bedrijf.</PageTitle>

        <List>
          {COMPANIES.map((company) => (
            <Item
              key={company.slug}
              onClick={() => switchTo(company)}
              disabled={!!switching}
            >
              <ItemInfo>
                <ItemName>{switching === company.slug ? 'Overschakelen…' : company.name}</ItemName>
              </ItemInfo>
              <ItemRight>
                <Chevron><ChevronRight size={14} strokeWidth={1.5} /></Chevron>
              </ItemRight>
            </Item>
          ))}
        </List>

        <BottomRow>
          <UserChip>
            <Avatar>{initials}</Avatar>
            <UserName>{superAdmin.username}</UserName>
          </UserChip>
          <LogoutBtn onClick={() => setShowLogout(true)}>Afmelden</LogoutBtn>
        </BottomRow>
      </Inner>

      {showLogout && (
        <Overlay onClick={() => setShowLogout(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalLabel>Bevestigen</ModalLabel>
            <ModalTitle>Afmelden</ModalTitle>
            <ModalText>Weet je zeker dat je wilt afmelden?</ModalText>
            <ModalButtons>
              <ModalBtn onClick={() => setShowLogout(false)}>Annuleren</ModalBtn>
              <ModalBtn $danger onClick={logout}>Afmelden</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
